import type { PayScheduleConfig, PayPeriod } from '../types/payroll-export';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toDateString(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function formatLabel(startStr: string, endStr: string): string {
  const [, sm, sd] = startStr.split('-').map(Number);
  const [, em, ed] = endStr.split('-').map(Number);
  return `${MONTH_NAMES[sm - 1]} ${sd} - ${MONTH_NAMES[em - 1]} ${ed}`;
}

interface PeriodBounds {
  readonly startYear: number;
  readonly startMonth: number; // 0-based
  readonly startDay: number;
  readonly endYear: number;
  readonly endMonth: number; // 0-based
  readonly endDay: number;
}

/**
 * Given a config and a reference date, determine which semi-monthly period the
 * date falls in and return the current period plus 5 previous periods (6 total),
 * ordered most-recent first.
 */
function detectCurrentPeriod(config: PayScheduleConfig, refDate: Date): PeriodBounds {
  const year = refDate.getUTCFullYear();
  const month = refDate.getUTCMonth(); // 0-based
  const day = refDate.getUTCDate();

  const { period1, period2 } = config;

  if (day >= period1.startDay && day <= period1.endDay) {
    // Period 1: starts and ends in the same month
    return {
      startYear: year,
      startMonth: month,
      startDay: period1.startDay,
      endYear: year,
      endMonth: month,
      endDay: period1.endDay,
    };
  }

  if (day >= period2.startDay) {
    if (period2.endDay >= period2.startDay) {
      // Same-month period (e.g., 16th-EOM): clamp endDay to actual last day of month
      const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      return {
        startYear: year,
        startMonth: month,
        startDay: period2.startDay,
        endYear: year,
        endMonth: month,
        endDay: Math.min(period2.endDay, lastDay),
      };
    }
    // Cross-month period (e.g., 20th to 4th of next month)
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    return {
      startYear: year,
      startMonth: month,
      startDay: period2.startDay,
      endYear: nextYear,
      endMonth: nextMonth,
      endDay: period2.endDay,
    };
  }

  // day < period1.startDay — we're in period 2 that started last month
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  if (period2.endDay >= period2.startDay) {
    // Same-month period that ended in the previous month
    const lastDay = new Date(Date.UTC(prevYear, prevMonth + 1, 0)).getUTCDate();
    return {
      startYear: prevYear,
      startMonth: prevMonth,
      startDay: period2.startDay,
      endYear: prevYear,
      endMonth: prevMonth,
      endDay: Math.min(period2.endDay, lastDay),
    };
  }
  return {
    startYear: prevYear,
    startMonth: prevMonth,
    startDay: period2.startDay,
    endYear: year,
    endMonth: month,
    endDay: period2.endDay,
  };
}

function buildPayPeriod(config: PayScheduleConfig, bounds: PeriodBounds): PayPeriod {
  const { period1, period2 } = config;
  const periodStart = toDateString(bounds.startYear, bounds.startMonth, bounds.startDay);
  const periodEnd = toDateString(bounds.endYear, bounds.endMonth, bounds.endDay);

  // Determine which period type this is based on startDay
  const isPeriod1 = bounds.startDay === period1.startDay;
  const paydayDay = isPeriod1 ? period1.payday : period2.payday;

  // For same-month period2, payday falls in the next calendar month
  const isSameMonthPeriod2 = !isPeriod1 && period2.endDay >= period2.startDay;
  const paydayMonth = isSameMonthPeriod2
    ? (bounds.endMonth === 11 ? 0 : bounds.endMonth + 1)
    : bounds.endMonth;
  const paydayYear = isSameMonthPeriod2
    ? (bounds.endMonth === 11 ? bounds.endYear + 1 : bounds.endYear)
    : bounds.endYear;
  const payday = toDateString(paydayYear, paydayMonth, paydayDay);

  const label = formatLabel(periodStart, periodEnd);
  const id = `${periodStart}_${periodEnd}`;

  return { id, periodStart, periodEnd, cutoffDate: periodEnd, payday, label };
}

/**
 * Step backwards by one period from the given bounds.
 * If current bounds represent period1 → previous is period2 (from prev month).
 * If current bounds represent period2 → previous is period1 (same month as period2 start).
 */
function previousPeriodBounds(config: PayScheduleConfig, current: PeriodBounds): PeriodBounds {
  const { period1, period2 } = config;
  const isPeriod1 = current.startDay === period1.startDay;

  if (isPeriod1) {
    // Go back to period2 of previous month
    const prevMonth = current.startMonth === 0 ? 11 : current.startMonth - 1;
    const prevYear = current.startMonth === 0 ? current.startYear - 1 : current.startYear;
    if (period2.endDay >= period2.startDay) {
      // Same-month period2: it ends in prevMonth, not the current month
      const lastDay = new Date(Date.UTC(prevYear, prevMonth + 1, 0)).getUTCDate();
      return {
        startYear: prevYear,
        startMonth: prevMonth,
        startDay: period2.startDay,
        endYear: prevYear,
        endMonth: prevMonth,
        endDay: Math.min(period2.endDay, lastDay),
      };
    }
    return {
      startYear: prevYear,
      startMonth: prevMonth,
      startDay: period2.startDay,
      endYear: current.startYear,
      endMonth: current.startMonth,
      endDay: period2.endDay,
    };
  }

  // Current is period2; go back to period1 of the same month as period2 start
  return {
    startYear: current.startYear,
    startMonth: current.startMonth,
    startDay: period1.startDay,
    endYear: current.startYear,
    endMonth: current.startMonth,
    endDay: period1.endDay,
  };
}

/**
 * Compute semi-monthly pay period bounds for a given reference date.
 *
 * Returns 6 periods: the current period containing referenceDate (index 0),
 * followed by the 5 preceding periods in reverse-chronological order.
 *
 * @param config - Pay schedule configuration describing the two semi-monthly periods.
 * @param referenceDate - The date used to identify the current period. Defaults to today.
 * @returns Readonly array of 6 PayPeriod objects, most-recent first.
 */
export function computePayPeriodBounds(
  config: PayScheduleConfig,
  referenceDate: Date = new Date(),
): readonly PayPeriod[] {
  const current = detectCurrentPeriod(config, referenceDate);
  const periods: PayPeriod[] = [buildPayPeriod(config, current)];

  let bounds = current;
  for (let i = 1; i < 6; i++) {
    bounds = previousPeriodBounds(config, bounds);
    periods.push(buildPayPeriod(config, bounds));
  }

  return periods;
}
